import { useCallback, useMemo, useState } from 'react';
import { formatPriceDisplay } from '@/util/formatting';
import { AI_VIRTUAL_STAGING_SLUG } from '@/components/property-assets-management/order-service-modal/constants';
import type { PhotoPaneItem } from '@/components/property-assets-management/order-service-modal/OrderServicePhotoPane';
import type { GroupedAssetRow } from '../utils/cartTransforms';

type Params = {
  row: GroupedAssetRow;
  updateAssetGroup: (p: { lineItemUUID?: string; lineAssetId: string; lineSlug: string; removedRefIds: string[]; note: string }) => Promise<void>;
  removeAssetItem: (lineItemUUID: string | undefined, assetId: string, slug: string) => Promise<void>;
  onClose: () => void;
};

export function useAssetCartEdit({ row, updateAssetGroup, removeAssetItem, onClose }: Params) {
  // The grouped row IS one cart line; its photos come from assetRefs.
  // Memoised so callbacks that list `refs` in their deps don't re-fire
  // every render when the row reference is stable.
  const refs = useMemo(() => row.assetRefs ?? [], [row.assetRefs]);
  const [note, setNote] = useState(row.additionalNotes ?? '');
  const [remainingIds, setRemainingIds] = useState<string[]>(refs.map((r) => r.assetId));
  const [saving, setSaving] = useState(false);

  const serviceName = row.service?.name || 'Image Edit';
  const lineSlug = row.service?.shoppingCartSlug ?? '';
  const noteRequired = lineSlug !== AI_VIRTUAL_STAGING_SLUG;
  const remainingSet = useMemo(() => new Set(remainingIds), [remainingIds]);
  // Stable line identity (DV-2131 §3): prefer the top-level lineItemUUID; keep the singular
  // assetId (falling back to the first ref) as the legacy composite key for lines without one.
  const lineItemUUID = row.lineItemUUID;
  const lineAssetId = row.assetId ?? refs[0]?.assetId ?? '';

  // Per-photo unit price = line total ÷ photo count. row.price is the whole-line total, so the
  // header chip and the per-image rows must divide by the quantity to show the per-photo cost
  // (matches the add modal, which shows the unit price per photo).
  const unitPriceText = `$${formatPriceDisplay(row.price / (row.quantity || 1))}`;

  const photos: PhotoPaneItem[] = useMemo(
    () =>
      refs
        .filter((r) => remainingSet.has(r.assetId))
        .map((r) => ({
          id: r.assetId,
          thumbnailUrl: r.assetUrl ?? '',
          caption: serviceName,
          lineLabel: unitPriceText,
        })),
    [refs, remainingSet, serviceName, unitPriceText],
  );

  const removePhoto = useCallback(
    (id: string) => {
      // Removing the last remaining photo deletes the whole line. updateCart
      // only MERGES (never removes an absent line), so this must go through the
      // dedicated delete endpoint, not updateAssetGroup-with-empty-refs.
      if (remainingIds.length === 1 && remainingIds[0] === id) {
        void removeAssetItem(lineItemUUID, lineAssetId, lineSlug);
        onClose();
        return;
      }
      setRemainingIds((ids) => ids.filter((existing) => existing !== id));
    },
    [remainingIds, removeAssetItem, lineItemUUID, lineAssetId, lineSlug, onClose],
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const removedRefIds = refs.map((r) => r.assetId).filter((id) => !remainingSet.has(id));
      await updateAssetGroup({ lineItemUUID, lineAssetId, lineSlug, removedRefIds, note });
      onClose();
    } finally {
      setSaving(false);
    }
  }, [refs, remainingSet, lineItemUUID, lineAssetId, lineSlug, updateAssetGroup, note, onClose]);

  const cancel = useCallback(() => onClose(), [onClose]);

  return {
    serviceName,
    unitPriceText,
    addressLine: row.propertyAddress,
    photos,
    note,
    setNote,
    noteRequired,
    canSave: !noteRequired || note.trim().length > 0,
    saving,
    removePhoto,
    save,
    cancel,
  };
}
